import { Check, ChevronDown } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { ExportOptionsDropdownProps } from '@/types';
import useTranslation from '@/hooks/useTranslation';

/**
 * Tiny glyph that communicates the row grain of an export at a glance:
 * "wide" = one row per respondent (answers spread across columns),
 * "tall" = one row per message (the conversation stacked top-to-bottom).
 * This is the pre-click cue that stops the wide "answers in columns" export
 * from being confused with the stacked per-message export.
 */
function ShapeGlyph({ shape }: { shape?: 'wide' | 'tall' }) {
  if (shape === 'tall') {
    return (
      <svg width="16" height="16" viewBox="0 0 16 16" aria-hidden="true" className="shrink-0">
        <rect
          x="2"
          y="1"
          width="12"
          height="14"
          rx="1.5"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.2"
        />
        <rect x="2" y="1" width="3.6" height="14" rx="1.5" fill="currentColor" opacity="0.18" />
        <line x1="2" y1="6" x2="14" y2="6" stroke="currentColor" strokeWidth="1" />
        <line x1="2" y1="10.5" x2="14" y2="10.5" stroke="currentColor" strokeWidth="1" />
      </svg>
    );
  }
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" aria-hidden="true" className="shrink-0">
      <rect
        x="1"
        y="2"
        width="14"
        height="12"
        rx="1.5"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.2"
      />
      <rect x="1" y="2" width="14" height="3.2" rx="1.5" fill="currentColor" opacity="0.18" />
      <line x1="6" y1="2" x2="6" y2="14" stroke="currentColor" strokeWidth="1" />
      <line x1="10.5" y1="2" x2="10.5" y2="14" stroke="currentColor" strokeWidth="1" />
    </svg>
  );
}

function ExportOptionsDropdown({
  exportOptions,
  selectedOption,
  setSelectedOption,
}: ExportOptionsDropdownProps) {
  const { lang } = useTranslation();

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          className="font-light border border-gray-300 justify-between max-w-64 w-full"
        >
          <span
            className="truncate overflow-hidden"
            title={lang.get(selectedOption ? selectedOption.label : 'msg.pleaseSelectExportOption')}
          >
            {lang.get(selectedOption ? selectedOption.label : 'msg.pleaseSelectExportOption')}
          </span>
          <ChevronDown className="w-4 h-4 ml-4 flex-shrink-0" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent className="w-80 max-w-[22rem]">
        {exportOptions?.map((option) => {
          const isSelected = option.value === selectedOption?.value;
          return (
            <DropdownMenuItem
              key={option.value}
              className={`flex items-start gap-3 px-3 py-2.5 cursor-pointer ${
                isSelected ? 'bg-gray-100' : ''
              }`}
              onClick={() => setSelectedOption(option)}
            >
              <span className="mt-0.5 text-gray-500">
                <ShapeGlyph shape={option.shape} />
              </span>
              <span className="flex flex-col min-w-0 flex-1">
                <span className="text-sm font-medium text-dark-text">{lang.get(option.label)}</span>
                {option.description && (
                  <span className="text-xs text-gray-500 mt-0.5 leading-snug whitespace-normal">
                    {option.description}
                  </span>
                )}
              </span>
              {isSelected && <Check className="w-4 h-4 mt-0.5 shrink-0 text-primary" />}
            </DropdownMenuItem>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export default ExportOptionsDropdown;
